import { AlertTriangle } from 'lucide-react'
import { formatIndianDateTime } from '@/shared/utils/dateTime'

export interface MeetingConflict {
  meetingId: string
  title: string
  scheduledAt: string
  endsAt: string
  durationMinutes: number
  leadName: string | null
  leadCompany: string | null
  hostName: string | null
  involvedNames: string[]
}

/**
 * Double-booking warning: lists, per already-booked meeting, who is
 * involved, with which client and when. Rendered while the user is still
 * filling the schedule form — booking is blocked until the conflict clears.
 */
export function MeetingConflictNotice({ conflicts }: { conflicts: MeetingConflict[] }) {
  if (conflicts.length === 0) return null
  return (
    <div className="rounded-xl border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 p-3 space-y-1.5">
      <div className="flex items-center gap-1.5 text-xs font-bold text-red-800 dark:text-red-300">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        <span>Schedule conflict — someone you are inviting is already booked:</span>
      </div>
      <ul className="space-y-1">
        {conflicts.map((c) => {
          const client = c.leadCompany || c.leadName
          return (
            <li key={c.meetingId} className="text-[11px] text-red-900 dark:text-red-200 leading-snug">
              <span className="font-semibold">{c.involvedNames.join(', ') || 'A participant'}</span>
              {' is in '}
              <span className="font-semibold">“{c.title}”</span>
              {client ? <> with <span className="font-semibold">{client}</span></> : null}
              {' at '}
              <span className="font-semibold">{formatIndianDateTime(c.scheduledAt)}</span>
              {c.hostName ? <> (host: {c.hostName})</> : null}
            </li>
          )
        })}
      </ul>
      <p className="text-[10px] text-red-700/80 dark:text-red-400/80">
        Booking is blocked while there is a conflict — pick a different time.
      </p>
    </div>
  )
}
